import { routeInspectionsManager } from "@/setup/routes/inspection.routes";
import { buildUploadUrl } from "@/shared/models/Inspection";
import {
	dxtToLocalServerTime,
	getMomentTz,
	useToken,
} from "@dexteel/mesf-core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
	Box,
	Button,
	Divider,
	IconButton,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InspectionHeader } from "../../models/Inspection";
import { upsertInspection } from "../../repositories/InspectionRepository";
import { DrawingPanel } from "./DrawingPanel";
import { SheetLastUpdate } from "../InspectionSheetPage";

const moment = getMomentTz();

const DRAWING_WIDTH_STORAGE_KEY = "inspection-sheet-drawing-width";
const DEFAULT_DRAWING_WIDTH = 320;

const readSavedDrawingWidth = () => {
	try {
		const saved = Number(localStorage.getItem(DRAWING_WIDTH_STORAGE_KEY));
		if (Number.isFinite(saved) && saved >= DEFAULT_DRAWING_WIDTH) return saved;
	} catch (e) {
		console.error("Error loading drawing width:", e);
	}
	return DEFAULT_DRAWING_WIDTH;
};

const saveDrawingWidth = (width: number) => {
	try {
		localStorage.setItem(DRAWING_WIDTH_STORAGE_KEY, String(width));
	} catch (e) {
		console.error("Error saving drawing width:", e);
	}
};

const Fact = ({
	label,
	value,
	testId,
}: {
	label: string;
	value: ReactNode;
	testId?: string;
}) => (
	<Box sx={{ userSelect: "none" }}>
		<Typography
			variant="caption"
			color="text.secondary"
			sx={{ fontWeight: 600 }}
		>
			{label}
		</Typography>
		<Typography variant="body2" fontWeight={600} data-test-id={testId}>
			{value}
		</Typography>
	</Box>
);

const FieldLabel = ({ children }: { children: ReactNode }) => (
	<Typography
		variant="caption"
		color="text.secondary"
		sx={{ fontWeight: 600, display: "block", mb: 0.25 }}
	>
		{children}
	</Typography>
);

type Props = {
	inspection: InspectionHeader | null;
	lastUpdate: SheetLastUpdate | null;
	onError: (message: string) => void;
	onPointAtLastUpdate: (isPointing: boolean) => void;
};

export const InspectionSheetHeader = ({
	inspection,
	lastUpdate,
	onError,
	onPointAtLastUpdate,
}: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { getFirstName, getLastName, getUsername } = useToken();

	const rowRef = useRef<HTMLDivElement>(null);

	const [comments, setComments] = useState("");
	const [drawingWidth, setDrawingWidth] = useState(readSavedDrawingWidth);
	const [drawingRatio, setDrawingRatio] = useState<number | null>(null);

	const drawingUrl = buildUploadUrl(inspection?.DrawingPath ?? null);
	const hasCommentChanges = comments !== (inspection?.Comments ?? "");
	const isEditable = inspection?.IsEditable ?? false;

	const currentUserName =
		[getFirstName(), getLastName()].filter(Boolean).join(" ") || getUsername();
	const inspectorName = inspection?.InspectorName ?? currentUserName;

	const clampDrawingWidth = (width: number, ratio: number) => {
		const rowWidth = rowRef.current?.clientWidth ?? width;
		const maxByRow = rowWidth * 0.6;
		const maxByHeight = window.innerHeight * 0.45 * ratio;
		const max = Math.max(
			DEFAULT_DRAWING_WIDTH,
			Math.min(maxByRow, maxByHeight),
		);

		return Math.round(Math.min(Math.max(width, DEFAULT_DRAWING_WIDTH), max));
	};

	const changeDrawingWidth = (width: number, isFinal = false) => {
		if (drawingRatio === null) return;

		const next = clampDrawingWidth(width, drawingRatio);
		setDrawingWidth(next);
		if (isFinal) saveDrawingWidth(next);
	};

	const changeDrawingRatio = (ratio: number) => {
		setDrawingRatio(ratio);
		setDrawingWidth((current) => clampDrawingWidth(current, ratio));
	};

	const resetDrawingSize = () => {
		if (drawingRatio === null) return;

		const next = clampDrawingWidth(DEFAULT_DRAWING_WIDTH, drawingRatio);
		setDrawingWidth(next);
		saveDrawingWidth(next);
	};

	const drawingHeight =
		drawingRatio === null ? null : Math.round(drawingWidth / drawingRatio);

	const inspectionMutation = useMutation(upsertInspection, {
		meta: { successMessage: "The inspection was updated successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["inspection"] });
			await queryClient.invalidateQueries({ queryKey: ["inspections"] });
		},
		onError: (mutationError: Error) => onError(mutationError.message),
	});

	const saveComments = () => {
		if (!inspection) return;
		inspectionMutation.mutate({
			inspectionId: inspection.InspectionId,
			comments,
		});
	};

	useEffect(() => {
		setComments(inspection?.Comments ?? "");
	}, [inspection?.InspectionId, inspection?.Comments]);

	return (
		<>
			<Box ref={rowRef} sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
				<Paper
					elevation={2}
					sx={{
						flexGrow: 1,
						minWidth: 0,
						borderTop: 3,
						borderColor: "primary.main",
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box
						sx={{
							p: 2,
							flexGrow: 1,
							display: "flex",
							alignItems: "flex-start",
							gap: 2,
						}}
					>
						<IconButton
							title="Back to the manager"
							size="small"
							onClick={() =>
								navigate(
									`${routeInspectionsManager}/${inspection?.ShiftId ?? ""}`,
								)
							}
							sx={{ border: "1px solid", borderColor: "divider", mt: 0.5 }}
						>
							<ArrowBackIcon fontSize="small" />
						</IconButton>

						<Box sx={{ flexGrow: 1, minWidth: 0 }}>
							<Typography variant="h5" fontWeight={700}>
								{inspection?.SectionName ?? ""}
							</Typography>
							{inspection && (
								<Stack
									direction="row"
									spacing={4}
									useFlexGap
									sx={{ mt: 1, flexWrap: "wrap" }}
								>
									<Fact
										label="REV"
										value={`${inspection.Rev}`}
										testId="sheet-rev"
									/>
									<Fact
										label="DATE"
										value={moment
											.utc(inspection.ProductionDate)
											.format("ddd MM/DD/YYYY")}
									/>
									<Fact
										label="SHIFT"
										value={`${inspection.Shift} - ${inspection.Crew}`}
									/>
									{lastUpdate && (
										<Fact
											label="LAST UPDATE"
											value={
												<Tooltip title="The reading this save wrote is marked on the sheet">
													<Box
														component="span"
														onMouseEnter={() => onPointAtLastUpdate(true)}
														onMouseLeave={() => onPointAtLastUpdate(false)}
													>
														{`${lastUpdate.by ?? "-"} · ${dxtToLocalServerTime(
															lastUpdate.at,
															"MM/dd HH:mm",
														)}`}
													</Box>
												</Tooltip>
											}
										/>
									)}
								</Stack>
							)}
						</Box>
					</Box>

					<Divider />

					<Box
						sx={{
							px: 2,
							py: 1.5,
							backgroundColor: (theme) => theme.palette.grey[50],
							display: "flex",
							alignItems: "flex-end",
							gap: 2,
						}}
					>
						<Box sx={{ width: 260 }}>
							<FieldLabel>INSPECTOR</FieldLabel>
							<TextField
								value={inspectorName}
								fullWidth
								size="small"
								InputProps={{ readOnly: true }}
								sx={{
									display: "block",
									backgroundColor: (theme) => theme.palette.background.paper,
								}}
							/>
						</Box>

						<Box sx={{ flexGrow: 1 }}>
							<FieldLabel>COMMENTS</FieldLabel>
							<TextField
								value={comments}
								onChange={(event) => setComments(event.target.value)}
								InputProps={{ readOnly: !isEditable }}
								onKeyDown={(event) => {
									if (
										event.key === "Enter" &&
										(event.ctrlKey || event.metaKey)
									) {
										event.preventDefault();
										if (
											isEditable &&
											hasCommentChanges &&
											!inspectionMutation.isLoading
										)
											saveComments();
										return;
									}

									if (event.key === "Escape") {
										event.preventDefault();
										setComments(inspection?.Comments ?? "");
										event.currentTarget.blur();
									}
								}}
								placeholder="Optional note for the shift... (Ctrl+Enter to save)"
								multiline
								minRows={1}
								maxRows={4}
								fullWidth
								size="small"
								autoComplete="off"
								sx={{
									display: "block",
									backgroundColor: (theme) => theme.palette.background.paper,
								}}
							/>
						</Box>

						<Button
							variant="outlined"
							sx={{ height: 40, flexShrink: 0 }}
							disabled={
								!isEditable ||
								!hasCommentChanges ||
								inspectionMutation.isLoading
							}
							onClick={saveComments}
						>
							Save
						</Button>
					</Box>
				</Paper>

				<Paper
					elevation={2}
					sx={{
						width: drawingUrl ? drawingWidth : DEFAULT_DRAWING_WIDTH,
						minHeight: drawingUrl && drawingHeight ? drawingHeight : undefined,
						flexShrink: 0,
						borderTop: 3,
						borderColor: "primary.main",
						overflow: "hidden",
						display: "flex",
					}}
				>
					<DrawingPanel
						url={drawingUrl}
						fileName={inspection?.DrawingFileName ?? null}
						sectionName={inspection?.SectionName ?? ""}
						rev={inspection?.Rev ?? 0}
						width={drawingWidth}
						canResize={drawingRatio !== null}
						onWidthChange={changeDrawingWidth}
						onRatioChange={changeDrawingRatio}
						onResetSize={resetDrawingSize}
					/>
				</Paper>
			</Box>
		</>
	);
};
